/**
 * Validates that the date is an array with two valid Date objects
 * @param date - The date range to validate
 * @returns true if valid, false otherwise
 */
export const isValidDateRange = (date: any): date is [Date, Date] => {
  return (
    date &&
    Array.isArray(date) &&
    date.length === 2 &&
    date[0] instanceof Date &&
    date[1] instanceof Date &&
    !isNaN(date[0].getTime()) &&
    !isNaN(date[1].getTime())
  );
};

/**
 * Creates a validated date change handler for components
 * @param setState - The setState function to call when date is valid
 * @returns A function that validates and sets the date
 */
export const createDateChangeHandler = (
  setState: (date: [Date, Date]) => void
) => {
  return (date: any) => {
    if (isValidDateRange(date)) {
      setState(date);
    }
  };
};

/**
 * Returns the inclusive number of days covered by a [from, to] date range.
 * Normalizes both dates to midnight first, since date pickers can return the
 * "to" date at end-of-day (23:59:59.999), which would otherwise throw off
 * the day count by a day.
 */
export const getDateRangeDayCount = (date: [Date, Date]): number => {
  const msPerDay = 24 * 60 * 60 * 1000;
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return (
    Math.round((startOfDay(date[1]) - startOfDay(date[0])) / msPerDay) + 1
  );
};

/**
 * Checks whether a date range spans no more than maxDays (inclusive)
 */
export const isDateRangeWithinLimit = (
  date: [Date, Date],
  maxDays: number
): boolean => {
  return getDateRangeDayCount(date) <= maxDays;
};

/**
 * Clamps the "to" date of a range so the range spans at most maxDays (inclusive)
 */
export const clampDateRangeToLimit = (
  date: [Date, Date],
  maxDays: number
): [Date, Date] => {
  if (isDateRangeWithinLimit(date, maxDays)) {
    return date;
  }
  const clampedTo = new Date(date[0]);
  clampedTo.setDate(clampedTo.getDate() + maxDays - 1);
  return [date[0], clampedTo];
};
